// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import RandomEventManager from '../../utils/game/RandomEventManager';
import EventBus, { EVENT_TYPES } from '../../utils/system/EventBus';
import * as OrbitUtils from '../../utils/game/OrbitEventUtils';

/**
 * Custom hook for orbit entry/docking sequence and orbit exit logic
 * Extracted from NavigationPanel to reduce file size
 */
export const useOrbitManagement = ({
  selectedSystem,
  warpTimerRef,
  enrichLocationWithCoordinates,
  setCurrentLocation,
  setShowArrivalScreen,
  setShowOrbitScreen,
  setShowWarpEffect,
  setWarpDisengaging,
  setWarpElements,
  setWarpTravelTime,
  setWarpTravelProgress,
  setDockingProgress,
  setShowDockingSequence,
  setHasArrived,
  setActiveEvent,
  setExpandedMap
}) => {
  const handleEnterOrbit = () => {
    setShowArrivalScreen(false);

    const isStation = selectedSystem && selectedSystem.type === 'station';

    if (isStation) {
      setShowDockingSequence(true);
      setDockingProgress(0);

      EventBus.emit(EVENT_TYPES.DOCKING_INITIATED);

      const dockingDuration = 2000;
      const dockingInterval = 50;
      const dockingSteps = dockingDuration / dockingInterval;
      let currentStep = 0;

      const dockingTimer = setInterval(() => {
        currentStep++;
        const progress = (currentStep / dockingSteps) * 100;
        setDockingProgress(progress);

        if (currentStep >= dockingSteps) {
          clearInterval(dockingTimer);

          setShowDockingSequence(false);
          setDockingProgress(0);

          EventBus.emit(EVENT_TYPES.DOCKING_COMPLETE);

          setShowOrbitScreen(true);
          setCurrentLocation(enrichLocationWithCoordinates(OrbitUtils.createOrbitLocation(selectedSystem)));
          setActiveEvent(null);

          EventBus.emit(EVENT_TYPES.ENTER_ORBIT);
        }
      }, dockingInterval);
    } else {
      setShowOrbitScreen(true);
      setCurrentLocation(enrichLocationWithCoordinates(OrbitUtils.createOrbitLocation(selectedSystem)));
      setActiveEvent(null);

      EventBus.emit(EVENT_TYPES.ENTER_ORBIT);
    }
  };

  const handleLeaveOrbit = () => {
    setActiveEvent(null);
    RandomEventManager.stop();
    setShowOrbitScreen(false);
    setHasArrived(false);
    setShowWarpEffect(false);
    setWarpDisengaging(false);
    setWarpElements([]);
    setWarpTravelTime(0);
    setWarpTravelProgress(0);

    if (warpTimerRef.current) {
      clearInterval(warpTimerRef.current);
      warpTimerRef.current = null;
    }

    // Leaving orbit should drop the player into the full galactic
    // navigation map (current system), not the small cross-universe
    // "UNIVERSE TRAVERSAL MAP" widget NavigationPanel shows by default.
    if (setExpandedMap) setExpandedMap(true);

    EventBus.emit(EVENT_TYPES.EXIT_ORBIT);
  };

  return { handleEnterOrbit, handleLeaveOrbit };
};
